/**
 * MypagePayContainer
 * --
 */
import React from 'react';
import MypagePayPresenter from './MypagePayPresenter';
import { useUserAction, useUserStack } from 'store';
import { useCancelChargeJmPay, useGetJmPays } from 'hooks/JmPayHooks';
import dayjs from 'dayjs';
import Formatter from 'utils/Formatter';
import MessageAlert, {
  jsonToQueryString,
  openPopup,
  DOMAIN_URL,
  DEPLOY_TYPE,
} from 'utils';

const MypagePayContainer = (props) => {
  /* ===== ROUTE ===== */
  /* ===== PROPS ===== */
  const { screenSize } = props;
  /* ===== VARIABLE ===== */
  /* ===== Store ===== */
  const userInfo = useUserStack();
  const userAction = useUserAction();

  /* ===== Mutate ===== */
  const { cancelChargeJmPay } = useCancelChargeJmPay();

  /* ===== STATE ===== */
  const today = dayjs();
  // Period Date
  const [selected, setSelected] = React.useState([
    today.subtract(1, 'month').startOf('day'),
    today.endOf('day'),
  ]);
  // loading
  const [isLoading, setIsLoading] = React.useState(false);

  const [isOpened, setIsOpened] = React.useState(false);

  /* ===== Data ===== */
  const { data: jmpayHistory, refetch } = useGetJmPays(
    {
      user_id: userInfo.user_id,
      start_date: selected[0].unix(),
      end_date: selected[1].unix(),
    },
    {
      enabled: !!userInfo,
      select: (res) => {
        if (res.status !== 200) return [];

        return res.data;
      },
    }
  );

  /* ===== FUNCTION ===== */
  /**
   * 팝업 열기
   * --
   * @param {string} type
   * @param {object} data
   */
  const handleOpenPopup = (type, data) => {
    const query = jsonToQueryString({
      type: type,
      ...data,
    });

    setIsOpened(true);
    openPopup(`/common/jmpay/charging${query}`);
  };

  /**
   * 제이엠페이 충전 취소
   * --
   * @param {object} d
   * @returns
   */
  const handleCancelPay = async (d = null) => {
    if (!d) {
      MessageAlert.info('충전 결제를 취소할 수 없습니다.');
      return;
    }

    try {
      const { payment_info, ...remain } = d;

      // NOTE: 제이엠페이 취소 처리
      const { status, data } = await cancelChargeJmPay({
        jm_pay_id: remain?.jm_pay_id,
        user_id: remain?.user_id,
      });

      if (status !== 200) {
        MessageAlert.info('충전 결제를 취소 할 수 없습니다.');
        return;
      }

      const newUserData = {
        ...userInfo,
        jm_pay: data?.user?.jm_pay,
      };
      userAction.setUser(newUserData);

      MessageAlert.success('충전이 취소되었습니다.');
      refetch();
    } catch (err) {
      MessageAlert.error('충전 취소중 에러가 발생하였습니다.');
      console.error('[handleCancelPay] Error!! ', err);
    } finally {
      setTimeout(() => {
        setIsLoading(false);
      }, 1000);
    }
  };

  /* ===== HOOKS ===== */
  /**
   * JM Pay 내역 정제
   * --
   */
  const JmPayHistory = React.useMemo(() => {
    const newHistory = jmpayHistory
      ?.map((history) => ({
        ...history,
        exp: dayjs.unix(history.created_at).add(7, 'day'),
      }))
      ?.sort((a, b) => b?.created_at - a?.created_at);

    const exp = dayjs().subtract(7, 'day').unix();

    const newHistories = newHistory?.map((history, index) => {
      const canRefund =
        index === 0 &&
        history.type.includes('CHARGE') &&
        history.created_at > exp
          ? 'Y'
          : 'N';

      return {
        ...history,
        canRefund,
      };
    });

    return newHistories;
  }, [jmpayHistory]);

  /**
   * 팝업 메세지 감지
   * --
   */
  React.useEffect(() => {
    // 메시지 수신 핸들러
    const handleMessage = (event) => {
      const allowedOrigins = [
        `http://${DOMAIN_URL}${DEPLOY_TYPE === 'local' ? ':3000' : ''}`,
        `https://${DOMAIN_URL}${DEPLOY_TYPE === 'local' ? ':3000' : ''}`,
        `http://mall.${DOMAIN_URL}${DEPLOY_TYPE === 'local' ? ':3000' : ''}`,
        `https://mall.${DOMAIN_URL}${DEPLOY_TYPE === 'local' ? ':3000' : ''}`,
        `https://jm-coffee.${DOMAIN_URL}${
          DEPLOY_TYPE === 'local' ? ':3000' : ''
        }`,
        `https://mall-jm-coffee.${DOMAIN_URL}${
          DEPLOY_TYPE === 'local' ? ':3000' : ''
        }`,
      ];

      // 보안 체크: 같은 origin에서 온 메시지만 허용
      if (!allowedOrigins.includes(event.origin)) {
        console.warn('허용되지 않은 origin:', event.origin);
        return;
      }

      const message = event.data;

      // 팝업에서 보낸 데이터만 처리
      // NOTE: 결제 성공
      if (message?.source === 'close') {
        const parseData = JSON.parse(message?.data);

        const newUserData = {
          ...userInfo,
          jm_pay: parseData?.user?.jm_pay,
        };
        userAction.setUser(newUserData);
      }
    };

    window.addEventListener('message', handleMessage);

    // 컴포넌트 언마운트 시 이벤트 리스너 제거
    return () => {
      window.removeEventListener('message', handleMessage);
    };
    // eslint-disable-next-line
  }, []);

  /* ===== RENDER ===== */
  return (
    <>
      <MypagePayPresenter
        user={userInfo}
        history={JmPayHistory}
        selected={selected}
        onSelect={setSelected}
        isLoading={isLoading}
        setIsLoading={setIsLoading}
        onOpenPopup={handleOpenPopup}
        screenSize={screenSize}
        isOpened={isOpened}
        onCancelPay={handleCancelPay}
      />
    </>
  );
};

export default MypagePayContainer;
